import { h, Host } from "@stencil/core";
import dayjs from "dayjs";
import { getUniqueId } from "../../utils/unique-id";
import { DATE_ISO } from "../date-picker/constants";
const baseClass = "ux-date-range-picker";
export class DateRangePicker {
    constructor() {
        this.dateFormat = "YYYY-MM-DD";
        this.disabled = false;
        this.firstWeekday = 1;
        this.inputId = undefined;
        this.kind = "default";
        this.language = undefined;
        this.maxDate = undefined;
        this.minDate = undefined;
        this.placeholder = undefined;
        this.resetText = undefined;
        this.todayText = undefined;
        this.rangeStartLabel = undefined;
        this.rangeStartTopLabel = undefined;
        this.rangeStartName = "start_date";
        this.rangeStartValue = "";
        this.rangeStartRequired = false;
        this.rangeStartError = false;
        this.rangeStartErrorMsg = null;
        this.rangeEndLabel = undefined;
        this.rangeEndTopLabel = undefined;
        this.rangeEndName = "end_date";
        this.rangeEndValue = "";
        this.rangeEndRequired = false;
        this.rangeEndError = false;
        this.rangeEndErrorMsg = null;
        this.rangeStartDay = null;
        this.rangeEndDay = null;
    }
    /** The component DOM node */
    domNode;
    startDatePicker = null;
    endDatePicker = null;
    /** event emitted when the the dates in the date range picker are changed.
     * The keys contain a string with the formatted date if a correct date has been selected,
     * otherwise they are set to null.
     */
    dateRangeChange;
    emitRangeChangeEvent = () => {
        this.dateRangeChange.emit({
            startDate: this.rangeStartDay !== null ? this.rangeStartDay.format(DATE_ISO) : null,
            endDate: this.rangeEndDay !== null ? this.rangeEndDay.format(DATE_ISO) : null
        });
    };
    setInputId() {
        this.inputId = this.inputId || getUniqueId();
    }
    setInitialRangeStart() {
        if (this.rangeStartValue !== "") {
            const startDate = dayjs(this.rangeStartValue, DATE_ISO);
            if (startDate.isValid()) {
                this.rangeStartDay = startDate;
                return;
            }
        }
        this.rangeStartDay = null;
    }
    setInitialRangeEnd() {
        if (this.rangeEndValue !== "") {
            const endDate = dayjs(this.rangeEndValue, DATE_ISO);
            if (endDate.isValid()) {
                this.rangeEndDay = endDate;
                return;
            }
        }
        this.rangeEndDay = null;
    }
    // @ts-expect-error Check for typing
    handleDateSelect(event) {
        const { value } = event.detail;
        const datePicker = event.target;
        const { startDatePicker, endDatePicker } = this;
        const activeDay = value === "" ? null : dayjs(value);
        const range = this.getActiveRange(datePicker, activeDay);
        if (range.flipped) {
            event.preventDefault();
        }
        this.rangeStartDay = range.start;
        this.rangeEndDay = range.end;
        if (this.rangeStartDay !== null) {
            // @ts-expect-error Check for typing
            startDatePicker.selectDate(this.rangeStartDay.format(DATE_ISO));
        }
        if (this.rangeEndDay !== null) {
            // @ts-expect-error Check for typing
            endDatePicker.selectDate(this.rangeEndDay.format(DATE_ISO));
        }
        this.emitRangeChangeEvent();
    }
    // @ts-expect-error Check for typing
    handleDateHover(event) {
        const { value } = event.detail;
        const datePicker = event.target;
        const activeDay = value === "" ? null : dayjs(value);
        const range = this.getActiveRange(datePicker, activeDay);
        datePicker.rangeStart = range.start === null ? "" : range.start.format(DATE_ISO);
        datePicker.rangeEnd = range.end === null ? "" : range.end.format(DATE_ISO);
    }
    getActiveRange(datePicker, activeDay) {
        const { startDatePicker, endDatePicker } = this;
        const range = {
            start: this.rangeStartDay,
            end: this.rangeEndDay,
            flipped: false
        };
        if (!activeDay?.isValid()) {
            if (datePicker === startDatePicker) {
                range.start = null;
            }
            else if (datePicker === endDatePicker) {
                range.end = null;
            }
            return range;
        }
        if (datePicker === startDatePicker) {
            if (range.end !== null && activeDay.isAfter(range.end, "d")) {
                range.end = activeDay;
                range.flipped = true;
            }
            else {
                range.start = activeDay;
            }
        }
        else if (datePicker === endDatePicker) {
            if (range.start !== null && activeDay.isBefore(range.start, "d")) {
                range.end = range.start;
                range.start = activeDay;
                range.flipped = true;
            }
            else {
                range.end = activeDay;
            }
        }
        return range;
    }
    componentWillLoad() {
        this.setInputId();
        this.setInitialRangeStart();
        this.setInitialRangeEnd();
    }
    render() {
        const { dateFormat, firstWeekday, inputId, kind, language, maxDate, minDate, placeholder, rangeStartDay, rangeStartLabel, rangeStartTopLabel, rangeStartName, rangeStartValue, rangeStartRequired, rangeStartError, rangeStartErrorMsg, rangeEndDay, rangeEndTopLabel, rangeEndLabel, rangeEndName, rangeEndValue, rangeEndRequired, rangeEndError, rangeEndErrorMsg, todayText, resetText } = this;
        const rangeStart = rangeStartDay !== null ? rangeStartDay.format(DATE_ISO) : "";
        const rangeEnd = rangeEndDay !== null ? rangeEndDay.format(DATE_ISO) : "";
        return (h(Host, { key: 'f7514b874de83ed0f059f4788767d4a986d53f36', class: {
                [baseClass]: true,
                [`${baseClass}--kind-default`]: kind === "default",
                [`${baseClass}--kind-form`]: kind === "form"
            } }, h("div", { key: 'cd7677227d8cf30e8e70bbea962c6b29dd72a1ec', class: `${baseClass}__container` }, h("ux-date-picker", { key: 'fb4701ab464cc0ef443ef17b2996b0a1fb21d01a', "date-format": dateFormat, "first-weekday": firstWeekday, value: rangeStartValue, "input-id": `${inputId}-start`, kind: kind, "top-label": rangeStartTopLabel, label: rangeStartLabel, language: language, "max-date": maxDate, "min-date": minDate, name: rangeStartName, placeholder: placeholder, "range-start": rangeStart, "range-end": rangeEnd, required: rangeStartRequired, error: rangeStartError, "error-msg": rangeStartErrorMsg, "reset-text": resetText, "today-text": todayText, ref: (el) => (el ? (this.startDatePicker = el) : null) }), h("span", { key: 'cb08b205daeb5340b1a8d9ba2183fc4baab69593', class: `${baseClass}__separator` }, "-"), h("ux-date-picker", { key: '621876c304348f262ce533263cd4056c69db3bc2', "date-format": dateFormat, "first-weekday": firstWeekday, value: rangeEndValue, "input-id": `${inputId}-end`, kind: kind, "top-label": rangeEndTopLabel, label: rangeEndLabel, language: language, "max-date": maxDate, "min-date": minDate, name: rangeEndName, placeholder: placeholder, "range-start": rangeStart, "range-end": rangeEnd, required: rangeEndRequired, error: rangeEndError, "error-msg": rangeEndErrorMsg, "reset-text": resetText, "today-text": todayText, ref: (el) => (el ? (this.endDatePicker = el) : null) }))));
    }
    static get is() { return "ux-date-range-picker"; }
    static get originalStyleUrls() {
        return {
            "$": ["date-range-picker.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["date-range-picker.css"]
        };
    }
    static get properties() {
        return {
            "dateFormat": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Format of the date value"
                },
                "attribute": "date-format",
                "reflect": false,
                "defaultValue": "\"YYYY-MM-DD\""
            },
            "disabled": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Flag that controls if the field is disabled"
                },
                "attribute": "disabled",
                "reflect": false,
                "defaultValue": "false"
            },
            "firstWeekday": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The weekday the calendar starts with (0=sunday, 1=monday, 6=saturday)"
                },
                "attribute": "first-weekday",
                "reflect": false,
                "defaultValue": "1"
            },
            "inputId": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Input id attribute"
                },
                "attribute": "input-id",
                "reflect": false
            },
            "kind": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "\"default\" | \"form\"",
                    "resolved": "\"default\" | \"form\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Controls the appearance of the control"
                },
                "attribute": "kind",
                "reflect": false,
                "defaultValue": "\"default\""
            },
            "language": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "The language in which texts are displayed"
                },
                "attribute": "language",
                "reflect": false
            },
            "maxDate": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Optionally, the date can be restricted to a maximum value (YYYY-MM-DD)"
                },
                "attribute": "max-date",
                "reflect": false
            },
            "minDate": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Optionally, the date can be restricted to a minimum value (YYYY-MM-DD)"
                },
                "attribute": "min-date",
                "reflect": false
            },
            "placeholder": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "the input placeholder text"
                },
                "attribute": "placeholder",
                "reflect": false
            },
            "resetText": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Label for the reset button"
                },
                "attribute": "reset-text",
                "reflect": false
            },
            "todayText": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Label for the today button"
                },
                "attribute": "today-text",
                "reflect": false
            },
            "rangeStartLabel": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Label text for the range end field"
                },
                "attribute": "range-start-label",
                "reflect": false
            },
            "rangeStartTopLabel": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Label text for the range end field"
                },
                "attribute": "range-start-top-label",
                "reflect": false
            },
            "rangeStartName": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Name for the range start input"
                },
                "attribute": "range-start-name",
                "reflect": false,
                "defaultValue": "\"start_date\""
            },
            "rangeStartValue": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Initial range start date (YYYY-MM-DD)"
                },
                "attribute": "range-start-value",
                "reflect": false,
                "defaultValue": "\"\""
            },
            "rangeStartRequired": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Flag that controls if the range start is mandatory"
                },
                "attribute": "range-start-required",
                "reflect": false,
                "defaultValue": "false"
            },
            "rangeStartError": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Indicates an invalid end date"
                },
                "attribute": "range-start-error",
                "reflect": false,
                "defaultValue": "false"
            },
            "rangeStartErrorMsg": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string | null",
                    "resolved": "null | string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Error message for the range start field"
                },
                "attribute": "range-start-error-msg",
                "reflect": false,
                "defaultValue": "null"
            },
            "rangeEndLabel": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Label text for the range end field"
                },
                "attribute": "range-end-label",
                "reflect": false
            },
            "rangeEndTopLabel": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Label text for the range end field"
                },
                "attribute": "range-end-top-label",
                "reflect": false
            },
            "rangeEndName": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Name for the range end input"
                },
                "attribute": "range-end-name",
                "reflect": false,
                "defaultValue": "\"end_date\""
            },
            "rangeEndValue": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Initial range end date (YYYY-MM-DD)"
                },
                "attribute": "range-end-value",
                "reflect": false,
                "defaultValue": "\"\""
            },
            "rangeEndRequired": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Flag that controls if the range end is mandatory"
                },
                "attribute": "range-end-required",
                "reflect": false,
                "defaultValue": "false"
            },
            "rangeEndError": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Indicates an invalid end date"
                },
                "attribute": "range-end-error",
                "reflect": false,
                "defaultValue": "false"
            },
            "rangeEndErrorMsg": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string | null",
                    "resolved": "null | string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Error message for the range end field"
                },
                "attribute": "range-end-error-msg",
                "reflect": false,
                "defaultValue": "null"
            }
        };
    }
    static get states() {
        return {
            "rangeStartDay": {},
            "rangeEndDay": {}
        };
    }
    static get events() {
        return [{
                "method": "dateRangeChange",
                "name": "uxDateRangeChange",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "event emitted when the the dates in the date range picker are changed.\nThe keys contain a string with the formatted date if a correct date has been selected,\notherwise they are set to null."
                },
                "complexType": {
                    "original": "{\n        startDate: string | null;\n        endDate: string | null;\n    }",
                    "resolved": "{ startDate: string | null; endDate: string | null; }",
                    "references": {}
                }
            }];
    }
    static get elementRef() { return "domNode"; }
    static get watchers() {
        return [{
                "propName": "inputId",
                "methodName": "setInputId"
            }, {
                "propName": "rangeStartValue",
                "methodName": "setInitialRangeStart"
            }, {
                "propName": "rangeEndValue",
                "methodName": "setInitialRangeEnd"
            }];
    }
    static get listeners() {
        return [{
                "name": "uxDateSelect",
                "method": "handleDateSelect",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "uxDateHover",
                "method": "handleDateHover",
                "target": undefined,
                "capture": false,
                "passive": false
            }];
    }
}
//# sourceMappingURL=date-range-picker.js.map
